import { animated, useSpring } from '@react-spring/web';
import cx from 'clsx';
import React, { useRef } from 'react';

import { ReactComponent as ArrowIcon } from '@assets/svg/nav-icons/ic-arrow.svg';

import style from './style.module.scss';

interface IProps {
  leftContent: React.ReactElement;
  children: React.ReactElement;
  isExpanded: boolean;
  variant?: 'filled';
  parentWrapRef?: React.RefObject<HTMLDivElement>;
  onToggle: (expanded: boolean) => void;
}

const Collapse: React.FC<IProps> = ({
  leftContent,
  children,
  isExpanded,
  variant,
  parentWrapRef,
  onToggle,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const contentHeight = contentRef.current ? contentRef.current.scrollHeight : 0;

  const expandStyles = useSpring({
    height: isExpanded ? contentHeight : 0,
    overflow: 'hidden',
    opacity: isExpanded ? 1 : 0,
    config: {
      duration: 300,
      tension: 120,
      friction: 14,
    },
    onRest: () => {
      if (isExpanded && parentWrapRef?.current)
        parentWrapRef.current.scroll({
          top: parentWrapRef.current.scrollHeight,
          behavior: 'smooth',
        });
    },
  });

  const handleToggle = () => {
    onToggle(!isExpanded);
  };

  return (
    <div
      className={cx({
        [style.filledCollapse]: variant === 'filled' && isExpanded,
      })}
    >
      <div onClick={handleToggle} className={style.toggler}>
        {leftContent}
        <div className={style.iconArrowContainer}>
          <ArrowIcon
            className={cx({
              [style.collapsedArrow]: isExpanded,
            })}
          />
        </div>
      </div>
      <animated.div
        style={{
          ...expandStyles,
        }}
        className={style.collapseChildrenContainer}
      >
        <div ref={contentRef}>{children}</div>
      </animated.div>
    </div>
  );
};

export default Collapse;
